import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Integrating Financial Well-Being into Life Balance',
    content: [
      '<strong>Learning Goal:</strong> Integrate financial health into a holistic view of well-being, practice value-based financial decision-making, and commit to sustainable, mindful, and community-oriented financial choices.',
      '<br/>',
      '<strong>Key Topics:</strong>',
      '• The connection between financial and mental well-being',
      '• How financial confidence improves relational stability and self-esteem',
      '• Aligning spending with values and priorities (value-based spending)',
      '• Sustainable living: redefining success beyond material wealth',
      '• Creating a long-term plan for balanced prosperity',
      '<br/>',
      '<strong>Discussion:</strong> True financial well-being extends beyond personal finances–it influences your relationships, self-worth, and ability to contribute to your community and the world.'
    ]
  },
  {
    title: 'Financial Well-Being & Mental Health',
    content: [
      'Financial stress and mental health are deeply interconnected–addressing one improves the other.',
      '<br/>',
      '<strong>How Financial Stress Affects Mental Health:</strong>',
      '• <strong>Anxiety:</strong> Constant worry about money creates chronic stress',
      '• <strong>Depression:</strong> Financial pressure can lead to feelings of hopelessness',
      '• <strong>Sleep issues:</strong> Money worries disrupt rest and recovery',
      '• <strong>Relationship strain:</strong> Financial disagreements affect partnerships',
      '• <strong>Reduced self-esteem:</strong> Financial struggles can make you question your worth',
      '<br/>',
      '<strong>How Mental Health Affects Financial Decisions:</strong>',
      '• <strong>Impulsive spending:</strong> Emotional spending to feel better temporarily',
      '• <strong>Avoidance:</strong> Ignoring bills and financial planning',
      '• <strong>Poor judgment:</strong> Stress impairs decision-making capacity',
      '• <strong>Reduced motivation:</strong> Depression makes financial tasks feel overwhelming',
      '<br/>',
      '<strong>The Positive Cycle:</strong>',
      '• Financial stability → Reduced stress → Better mental health',
      '• Improved mental health → Better financial decisions → More stability',
      '<br/>',
      '<strong>Key Insight:</strong> Financial well-being is a mental health issue as much as it is a financial one.'
    ]
  },
  {
    title: 'Financial Confidence & Relationships',
    content: [
      'Financial well-being strengthens relationships and builds self-esteem, creating a positive ripple effect in all areas of life.',
      '<br/>',
      '<strong>How Financial Confidence Improves Relationships:</strong>',
      '• <strong>Reduced conflict:</strong> Fewer arguments about money',
      '• <strong>Increased trust:</strong> Transparency about financial matters',
      '• <strong>Shared goals:</strong> Working together toward common objectives',
      '• <strong>Emotional security:</strong> Feeling safe to be vulnerable',
      '• <strong>Quality time:</strong> Less stress means more meaningful connections',
      '<br/>',
      '<strong>Building Self-Esteem Through Financial Health:</strong>',
      '• <strong>Accomplishment:</strong> Achieving financial goals builds confidence',
      '• <strong>Independence:</strong> Financial autonomy fosters self-respect',
      '• <strong>Future orientation:</strong> Planning ahead creates hope and purpose',
      '• <strong>Contribution:</strong> Ability to help others enhances self-worth',
      '<br/>',
      '<strong>Supporting Others:</strong>',
      '• <strong>Family:</strong> Teaching children healthy money habits',
      '• <strong>Friends:</strong> Offering support without judgment',
      '• <strong>Community:</strong> Contributing to causes that matter',
      '• <strong>Mentorship:</strong> Sharing knowledge with those who need it',
      '<br/>',
      '<strong>Takeaway:</strong> Financial well-being creates a foundation for stronger relationships and greater life satisfaction.'
    ]
  },
  {
    title: 'Value-Based Financial Decision Making',
    content: [
      'Aligning your spending with your core values creates financial decisions that enhance rather than diminish your well-being.',
      '<br/>',
      '<strong>Identifying Your Core Values:</strong>',
      '• <strong>Family:</strong> Relationships and loved ones',
      '• <strong>Health:</strong> Physical and mental well-being',
      '• <strong>Growth:</strong> Learning and personal development',
      '• <strong>Freedom:</strong> Independence and flexibility',
      '• <strong>Community:</strong> Contributing to others and society',
      '• <strong>Adventure:</strong> Experiences and exploration',
      '• <strong>Security:</strong> Stability and peace of mind',
      '• <strong>Creativity:</strong> Expression and innovation',
      '<br/>',
      '<strong>Value-Based Spending Questions:</strong>',
      '• Does this purchase align with my core values?',
      '• Will this bring lasting joy or just temporary pleasure?',
      '• Am I spending to impress others or fulfill my authentic self?',
      '• How does this fit into my long-term vision for my life?',
      '<br/>',
      '<strong>Practical Applications:</strong>',
      '• <strong>Subscription audit:</strong> Cancel services that don\'t serve your values',
      '• <strong>Purchase pause:</strong> Wait 24-48 hours before non-essential buys',
      '• <strong>Experience focus:</strong> Invest in memories over material possessions',
      '• <strong>Quality over quantity:</strong> Fewer, better things that truly matter',
      '<br/>',
      '<strong>Key Insight:</strong> When money flows toward your values, every financial decision becomes an act of self-expression.'
    ]
  },
  {
    title: 'Redefining Success & Sustainable Prosperity',
    content: [
      'True wealth extends beyond financial metrics–it includes health, relationships, purpose, and the ability to contribute to others.',
      '<br/>',
      '<strong>Beyond Material Wealth:</strong>',
      '• <strong>Time affluence:</strong> Freedom to spend time on what matters',
      '• <strong>Relationship wealth:</strong> Strong connections and support systems',
      '• <strong>Health wealth:</strong> Physical and mental well-being',
      '• <strong>Purpose wealth:</strong> Meaningful contribution to the world',
      '• <strong>Knowledge wealth:</strong> Continuous learning and growth',
      '<br/>',
      '<strong>Sustainable Prosperity Principles:</strong>',
      '• <strong>Enough mindset:</strong> Having enough rather than more than others',
      '• <strong>Generosity:</strong> Giving freely creates abundance',
      '• <strong>Long-term thinking:</strong> Decisions that benefit future generations',
      '• <strong>Balance:</strong> Work, rest, play, and contribution',
      '• <strong>Gratitude:</strong> Appreciating what you have while building for the future',
      '<br/>',
      '<strong>Creating Balanced Prosperity:</strong>',
      '• <strong>Financial:</strong> Security without excess',
      '• <strong>Social:</strong> Community and relationships',
      '• <strong>Environmental:</strong> Sustainable choices for the planet',
      '• <strong>Personal:</strong> Growth and self-actualization',
      '• <strong>Spiritual:</strong> Connection to something larger than yourself',
      '<br/>',
      '<strong>Legacy Planning:</strong> Consider how your financial choices impact future generations and the world you leave behind.'
    ],
    additionalSection: {
      title: 'Value Alignment Exercise',
      content: [
        'Complete this exercise to align your finances with your values:',
        '<br/>',
        '<strong>Step 1: List Your Top 5 Values</strong>',
        'Write down your core values (e.g., family, health, freedom, community, growth)',
        '<br/>',
        '<strong>Step 2: Review Recent Purchases</strong>',
        'List your last 5 major purchases or expenses',
        '<br/>',
        '<strong>Step 3: Value Alignment Check</strong>',
        'For each purchase, ask: "Does this align with my core values?"',
        '<br/>',
        '<strong>Step 4: Reflection</strong>',
        '• Which purchases felt truly aligned?',
        '• Which ones felt like they pulled you away from your values?',
        '• How does spending in alignment with values affect your well-being?',
        '<br/>',
        '<strong>Step 5: Future Planning</strong>',
        '• How can you adjust your spending to better reflect your values?',
        '• What\'s one value-aligned financial goal for the next month?',
        '<br/>',
        '<strong>Takeaway:</strong> When money serves your values, every financial decision becomes a step toward the life you want to live.'
      ]
    }
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed Module 5: Integrating Financial Well-Being into Life Balance! Now, let\'s reinforce your learning with a quick quiz.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about integrating financial well-being into holistic wellness.',
        'The quiz will help you review key concepts about value-based financial decision-making.'
      ]
    }
  }
];

const FinancialModule5Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('financialM5');
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/financial-module-5/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/financial-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div
          className="module-left financial-panel"
        >
          <div className="module-header">
            <h1>Module 5</h1>
            <p className="module-subtitle">Integrating Financial Well-Being into Life Balance</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Integrate financial health into a holistic view of well-being, practice value-based financial decision-making, and commit to sustainable, mindful, and community-oriented financial choices.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>

            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/financial-module-5/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialModule5Page;

